import { css } from "@emotion/css";

import { Icon } from "../../foundations/icons/Icon/Icon";

import { Text } from "../../foundations/typography/Text";
import { CascadiaColor } from "@certn/cascadia-tokens";
import { ReactNode } from "react";

type SectionSubHeadingProps = {
  title: string;
  className?: string;
  detailText?: string;
  isOpen?: boolean;
  isCollapsible?: boolean;
  tooltip?: React.ReactNode | null;
  testID?: string | null;
  onClick?: () => void;
  noWrap?: boolean;
  detailTextColor?: CascadiaColor;
  action?: ReactNode;
};

export function SectionSubHeading(props: SectionSubHeadingProps) {
  const {
    title,
    detailText,
    isOpen,
    isCollapsible,
    tooltip,
    testID,
    onClick,
    noWrap = false,
    detailTextColor,
    action,
  } = props;
  const styles = getStyles(isCollapsible || false);
  return (
    <div
      className={styles.wrapper}
      data-testid={testID ?? "section-subheading"}
    >
      <div
        className={css(
          styles.leftContainer,
          !isCollapsible ? { paddingLeft: "var(--space-3)" } : undefined,
          onClick ? { cursor: "pointer" } : undefined
        )}
        onClick={onClick}
      >
        {isCollapsible && (
          <Icon
            name="arrow_right"
            size="space-5"
            className={css({
              transform: isOpen ? "rotate(90deg) !important" : undefined,
              transition: "transform 0.2s ease-out",
              "@media print": { display: "none" },
            })}
          />
        )}
        <Text
          color="text-base"
          style="global/heading-medium"
          aria-label="Section Sub heading"
          testID="section-subheading-value"
          className={
            noWrap
              ? css({
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "1",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                })
              : undefined
          }
        >
          {title}
        </Text>
        {tooltip}
      </div>
      {detailText && (
        <div className={styles.rightContainer}>
          <Text
            color={detailTextColor ?? "text-weak"}
            style="global/info-label"
            aria-label="Detail Text"
            testID="section-subheading-detail-text"
          >
            {detailText}
          </Text>
        </div>
      )}
      {action && <div className={styles.rightContainer}>{action}</div>}
    </div>
  );
}

function getStyles(isCollapsible: boolean = false) {
  return {
    wrapper: css({
      boxSizing: "border-box",
      borderRadius: "var(--border-radius-2)",
      padding: "var(--space-1) var(--space-0)",
      maxHeight: "var(--space-8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: "var(--background-accent-neutral-weak)",
      "@media print": {
        paddingLeft: isCollapsible ? "var(--space-3)" : undefined,
      },
    }),
    leftContainer: css({
      display: "flex",
      alignItems: "center",
      gap: "var(--space-1-5)",
    }),
    rightContainer: css({
      paddingRight: "var(--space-3)",
      paddingLeft: "var(--space-6)",
      display: "flex",
      alignItems: "center",
    }),
  };
}
